// src/pages/Inventory/InventoryDashboard.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBoxes,
  FaExchangeAlt,
  FaMapMarkedAlt,
  FaBrain,
  FaClipboardList,
  FaSyncAlt,
  FaUtensils,
  FaLock,
  FaListAlt,
  FaFileImport,
} from "react-icons/fa";
import { useUserPermissions } from "@/hooks/useUserPermissions";

export default function InventoryDashboard() {
  const navigate = useNavigate();
  const perms = useUserPermissions();

  const {
    isAdmin = false,
    isDeveloper = false,
    canViewStockLevels = false,
    canViewStockMovements = false,
    canViewBins = false,
    canViewInsights = false,
    canViewUsageLogs = false,
    canManageReplenishment = false,
    canPlanEventMenus = false,
  } = perms || {};

  const isSuper = isAdmin || isDeveloper;

  const modules = [
    {
      title: "Stock Items",
      desc: "Browse and manage item master data.",
      icon: <FaBoxes className="text-3xl text-emerald-600" />,
      route: "/inventory/stock-items",
      allowed: canViewStockLevels || isSuper,
    },
    {
      title: "Stock Levels",
      desc: "Current on-hand by item & location.",
      icon: <FaBoxes className="text-3xl text-green-600" />,
      route: "/inventory/stock-levels",
      allowed: canViewStockLevels || isSuper,
    },
    {
      title: "Stock Movements",
      desc: "Receipts, issues, transfers, adjustments.",
      icon: <FaExchangeAlt className="text-3xl text-amber-600" />,
      route: "/inventory/stock-movements",
      allowed: canViewStockMovements || isSuper,
    },
    {
      title: "Bins & Locations",
      desc: "Warehouse/bin layout and assignments.",
      icon: <FaMapMarkedAlt className="text-3xl text-blue-600" />,
      route: "/inventory/bins-locations",
      allowed: canViewBins || isSuper,
    },
    {
      title: "AI Insights",
      desc: "Forecasts, anomalies, and reorder signals.",
      icon: <FaBrain className="text-3xl text-purple-600" />,
      route: "/inventory/ai-insights",
      allowed: canViewInsights || isSuper,
    },
    {
      title: "Usage Logs",
      desc: "Consumption history by day/item/cost centre.",
      icon: <FaClipboardList className="text-3xl text-cyan-600" />,
      route: "/inventory/usage-logs",
      allowed: canViewUsageLogs || isSuper,
    },
    {
      title: "Replenishment",
      desc: "Reorder proposals & purchase suggestions.",
      icon: <FaSyncAlt className="text-3xl text-pink-600" />,
      route: "/inventory/replenishment",
      allowed: canManageReplenishment || isSuper,
    },
    {
      title: "Supplier Catalog Import",
      desc: "Preview, match, and commit supplier catalog items.",
      icon: <FaFileImport className="text-3xl text-fuchsia-600" />,
      route: "/inventory/supplier-catalog",
      allowed: canViewStockLevels || canManageReplenishment || isSuper,
    },
    {
      title: "Menu Items (List)",
      desc: "Read-only list of dishes & drinks.",
      icon: <FaListAlt className="text-3xl text-indigo-600" />,
      route: "/menu/items",
      allowed: canPlanEventMenus || isSuper,
    },
    {
      title: "Menu Builder",
      desc: "Plan events and generate picklists.",
      icon: <FaUtensils className="text-3xl text-orange-600" />,
      route: "/inventory/menu-builder",
      allowed: canPlanEventMenus || isSuper,
    },
  ];

  const Tile = ({ title, desc, icon, route, allowed }) => {
    const enabled = !!allowed;

    return (
      <button
        onClick={() => enabled && navigate(route)}
        disabled={!enabled}
        className={[
          "group relative w-full text-left rounded-2xl p-6",
          "bg-white border border-slate-200",
          "shadow-sm",
          "transition-all duration-200",
          enabled
            ? "hover:-translate-y-1 hover:shadow-lg hover:border-sky-300"
            : "opacity-60 cursor-not-allowed bg-slate-50",
        ].join(" ")}
      >
        <div
          className={[
            "pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300",
            enabled ? "group-hover:opacity-100" : "",
          ].join(" ")}
          style={{
            boxShadow: enabled
              ? "0 0 0 1px rgba(14,165,233,0.10), 0 16px 40px -20px rgba(14,165,233,0.28)"
              : "none",
          }}
        />

        <div className="relative flex items-center gap-4">
          <div className="shrink-0 p-3 rounded-xl bg-slate-50 border border-slate-200 shadow-sm">
            {icon}
          </div>

          <div>
            <div className="text-xl font-semibold tracking-wide flex items-center gap-2 text-slate-900">
              {title}
              {!enabled && <FaLock className="text-slate-400 text-sm" />}
            </div>
            <p className="text-sm text-slate-600 mt-1">{desc}</p>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen px-6 md:px-12 py-10 text-slate-900 bg-gradient-to-br from-slate-50 via-white to-sky-50">
      <div className="max-w-7xl mx-auto space-y-8">
        <header className="space-y-1">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
            <span className="bg-gradient-to-r from-sky-600 via-cyan-600 to-violet-600 bg-clip-text text-transparent">
              Inventory Command Dashboard
            </span>
          </h1>
          <p className="text-slate-600">All inventory tools in one place.</p>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {modules.map((m) => (
            <Tile key={m.title} {...m} />
          ))}
        </div>
      </div>
    </div>
  );
}